import { useEffect } from "react";
import { values, every } from "lodash";

/**
This hook can be used to aggregate the validation of inputs spread out in different components.

Lets say one has a CreditCardForm (cc number, exp date, cvv) and a NameForm ( first, last )
Each form component is responsible for validating its internal state.
However, if a parent container needed to know if CreditCardForm and NameForm had valid input
then it is hard to extract that information.

With this custom hook, we can extract that information easily.
Each component form is responsible for registering a callback that will run the validation on the internal state.

@example
```
//checkout.js
import { runValidators } from "./useDistributedValidator";
import NameFormComponent from "./NameFormComponent";
import CreditCardInputComponent from "./CreditCardInputComponent";

CheckoutComponent() {
  const handleSubmit = () => {
    const isNameAndCCValid = runValidator();
    if(isNameAndCCValid) {
      save();
    } else {
      alert("One or more fields are invalid.")
    }
  }

  return (
    <>
      <CreditCardInputComponent />
      <NameFormComponent />
      <button onClick={handleSubmit}>Submit</button>
    </>
  );
}


//CreditCardInputComponent.js
import useDistributedValidator from "./useDistributedValidator";
CreditCardInputComponent() {
  useDistributedValidator(() => {
    return {
      number: validateCCNumberInput(),
      expiration: validateExpirationInput(),
      cvv: validateCVVInput()
    }
  })
  return <form>...</form>
}

//NameFormComponent.js
import useDistributedValidator from "./useDistributedValidator";
NameFormComponent() {
  useDistributedValidator(() => {
    return {
      firstName: validateFirstNameInput(),
      lastName: validateLastNameInput(),
    }
  })
  return <form>...</form>
}
```

*/

const callbacks = [];
export default function useDistributedValidator(callback) {
  useEffect(() => {
    callbacks.push(callback);

    return () => {
      const index = callbacks.indexOf(callback);
      callbacks.splice(index, 1);
    };
  }, [callback]);

  return null;
}

export function runValidators() {
  let validations = [];

  callbacks.forEach(validatorCallback => {
    const validationResult = validatorCallback();
    validations = validations.concat(values(validationResult));
  });

  const isValid = every(validations, hasValidValue => hasValidValue === true);

  return isValid;
}
